import { useCallback, useEffect, useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { observer } from 'mobx-react';
import cls from 'classnames';

import FancyInput from '@/common/components/inputs/FancyInput/FancyInput';
import Loader from '@/common/components/Loader/Loader';
import { DriveTreeItem } from '@/drive/entities/DriveTreeItem/DriveTreeItem';
import { useForwardDriveStore } from '@/forwarding/components/ForwardModal/ForwardDriveStore';
import DriveModalTree from '@/forwarding/components/ForwardModal/ForwardModalBody/DriveModalTree/DriveModalTree';
import { useDriveModalTreeStore } from '@/forwarding/components/ForwardModal/ForwardModalBody/DriveModalTree/DriveModalTreeStore';
import ForwardModalTabs from '@/forwarding/components/ForwardModal/ForwardModalBody/ForwardModalTabs/ForwardModalTabs';
import RecentDestinations from '@/forwarding/components/ForwardModal/ForwardModalBody/RecentDestinations/RecentDestinations';
import { useRecentDestinationsStore } from '@/forwarding/components/ForwardModal/ForwardModalBody/RecentDestinations/RecentDestinationsStore';
import ShareActions from '@/forwarding/components/ForwardModal/ForwardModalBody/ShareActions/ShareActions';
import ShareQrCode from '@/forwarding/components/ForwardModal/ForwardModalBody/ShareQrCode/ShareQrCode';
import { ForwardModalTab } from '@/forwarding/components/ForwardModal/ForwardModalTab';
import { useForwardShareStore } from '@/forwarding/components/ForwardModal/ForwardShareStore';
import { ShareActionType } from '@/forwarding/constants/ShareActionType';
import { ShareConfig } from '@/forwarding/entities/ShareConfig/ShareConfig';

import styles from './ForwardModalBody.module.css';
import { getAvailableTabs } from './ForwardModalBody.utils';

export interface ForwardModalBodyProps {
  hasDriveTree: boolean;
  activeTab: ForwardModalTab;
  shareConfig: Nullable<ShareConfig>;
  onTabChange: (tab: ForwardModalTab) => void;
  onRecentDestinationSelect: (destination: DriveTreeItem) => void;
  onShareActionSelect: (action: ShareActionType) => void;
}

function ForwardModalBody(props: ForwardModalBodyProps) {
  const { hasDriveTree, activeTab, shareConfig, onTabChange, onRecentDestinationSelect, onShareActionSelect } = props;
  const { isSubmitting } = useForwardDriveStore();
  const { isQrShown, hideQr } = useForwardShareStore();
  const { filter, changeFilter, allDestinations } = useRecentDestinationsStore();
  const { changeFilter: changeDriveTreeFilter } = useDriveModalTreeStore();

  const handleFilterChange = useCallback(
    (value: string) => {
      changeFilter(value);
      changeDriveTreeFilter(value);
    },
    [changeDriveTreeFilter, changeFilter],
  );

  const handleTabChange = useCallback(
    (tab: ForwardModalTab) => {
      hideQr();
      onTabChange(tab);
    },
    [hideQr, onTabChange],
  );

  const shareUrl = shareConfig?.getUrl() ?? null;
  const isQrCodeVisible = isQrShown && !!shareUrl;

  const hasRecentDestinations = allDestinations === null || allDestinations.length > 0;

  const tabs = useMemo(
    () => getAvailableTabs(hasDriveTree, !!shareConfig, hasRecentDestinations),
    [hasDriveTree, shareConfig, hasRecentDestinations],
  );

  useEffect(() => {
    if (tabs.length > 0 && !tabs.some(({ tab }) => tab === activeTab)) {
      onTabChange(tabs[0].tab);
    }
  }, [tabs, activeTab, onTabChange]);

  if (isQrCodeVisible) {
    return (
      <div className={styles.panel}>
        <ForwardModalTabs tabs={tabs} currentTab={activeTab} onTabChange={handleTabChange} />

        <ShareQrCode url={shareUrl!} onBack={hideQr} />
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <ForwardModalTabs tabs={tabs} currentTab={activeTab} onTabChange={handleTabChange} />

      {(activeTab === ForwardModalTab.Recent || activeTab === ForwardModalTab.DriveTree) && (
        <div className={styles.inputContainer}>
          <FancyInput
            endIcon="search"
            aria-label="Search destinations"
            placeholder="Search"
            value={filter}
            autoFocus={!isMobile}
            onChange={handleFilterChange}
            contentClassName={styles.searchInput}
          />
        </div>
      )}

      <div className={styles.content}>
        {activeTab === ForwardModalTab.Recent && (
          <div className={styles.recent}>
            <div className={styles.listContainer}>
              <RecentDestinations onSelect={onRecentDestinationSelect} onShareActionSelect={onShareActionSelect} />
            </div>
          </div>
        )}

        {activeTab === ForwardModalTab.DriveTree && <DriveModalTree />}

        {activeTab === ForwardModalTab.Share && shareConfig && (
          <ShareActions onSelect={onShareActionSelect} className={cls({ [styles.shareOnly]: tabs.length < 2 })} />
        )}

        {isSubmitting && (
          <div className={styles.loaderOverlay}>
            <Loader />
          </div>
        )}
      </div>
    </div>
  );
}

export default observer(ForwardModalBody);
